import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Lock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import IronVisionLogo from '../components/IronVisionLogo';

const AdminLogin = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.username, form.password);
      toast({ title: 'Welcome back', description: 'Signed in successfully.' });
      navigate('/admin', { replace: true });
    } catch (err) {
      toast({ title: 'Sign-in failed', description: err?.response?.data?.detail || 'Invalid credentials', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center px-6 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(rgba(34,211,238,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.5) 1px, transparent 1px)', backgroundSize: '64px 64px' }} />
      <div className="pointer-events-none absolute w-[500px] h-[500px] rounded-full blur-[120px] opacity-30 bg-cyan-500 top-10 -left-20" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <IronVisionLogo size={44} />
          <span className="text-white font-bold tracking-wider text-lg">IRON<span className="text-cyan-400">VISION</span></span>
        </div>
        <div className="rounded-2xl border border-cyan-400/15 bg-[#0d1424]/80 backdrop-blur p-8">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-5 h-5 text-cyan-400" />
            <h1 className="text-2xl font-bold text-white">Admin Sign In</h1>
          </div>
          <p className="text-sm text-slate-400 mb-6">Restricted to authorized personnel only.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-300 text-xs mb-1.5 block">Username</Label>
              <Input data-testid="admin-login-username" required autoFocus value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs mb-1.5 block">Password</Label>
              <Input data-testid="admin-login-password" required type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" />
            </div>
            <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full bg-cyan-400 hover:bg-cyan-300 disabled:opacity-70 text-slate-900 font-semibold rounded-md h-11 shadow-[0_0_24px_rgba(34,211,238,0.35)]">
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...</>) : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
